import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import FileUploader from "./fileUploader";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchBoxAccessToken } from "../../store/common/authSlice";
import { fetchCaseFilesByCaseId } from "../../store/common/fetchBoxFileSlice";

/** Matches tempurisui/uris-surgical-guide-form.html file zones. */
const SECTIONS = [
  {
    id: "maxilla",
    title: "Maxilla / Upper",
    subtitle: "Intraoral scan (IOS)",
    required: true,
    hint: ".dcm .stl .ply",
    accept: ".dcm,.stl,.ply",
    allowed: [".dcm", ".stl", ".ply"],
    folder: "Maxilla",
  },
  {
    id: "mandible",
    title: "Mandible / Lower",
    subtitle: "Intraoral scan (IOS)",
    required: true,
    hint: ".dcm .stl .ply",
    accept: ".dcm,.stl,.ply",
    allowed: [".dcm", ".stl", ".ply"],
    folder: "Mandible",
  },
  {
    id: "bite",
    title: "Bite / Additional data",
    subtitle: "Bite scan, photos, documents",
    required: false,
    hint: ".dcm .stl .ply · Additional: .jpg .jpeg .png .pdf .zip",
    accept: ".dcm,.stl,.ply,.jpg,.jpeg,.png,.pdf,.zip",
    allowed: [
      ".dcm",
      ".stl",
      ".ply",
      ".jpg",
      ".jpeg",
      ".png",
      ".pdf",
      ".zip",
    ],
    folder: "Bite",
  },
  {
    id: "cbct",
    title: "CBCT",
    subtitle: "DICOM, zipped",
    required: false,
    hint: ".zip only · Max size 2 GB · Optional for abutment follow-up",
    accept: ".zip,application/zip",
    allowed: [".zip"],
    folder: "CBCT",
  },
] as const;

type Props = {
  caseId: string;
  caseOwner: string;
  caseFolderId?: string | number | null;
};

const UrisCaseFiles = ({ caseId, caseOwner, caseFolderId }: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, boxAccessToken } = useAppSelector((state) => state.auth);
  const { caseFiles, fetching } = useAppSelector((state) => state.fileUpload);
  const clientFiles =
    caseFiles?.filter(
      (file) => file.file_type?.toLowerCase() === "client_upload",
    ) ?? [];
  const hasFiles = clientFiles.length > 0;

  useEffect(() => {
    if (!boxAccessToken) dispatch(fetchBoxAccessToken());
  }, [boxAccessToken, dispatch]);

  useEffect(() => {
    if (caseId && boxAccessToken) {
      dispatch(fetchCaseFilesByCaseId({ case_id: caseId }));
    }
  }, [caseId, boxAccessToken, dispatch]);

  return (
    <div className="uris-scope rounded-[14px] border border-[#2e2f34] bg-[#1d1e21] p-5 text-[#eceaeb]">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3 border-b border-[#2e2f34] pb-3.5">
        <div>
          <p className="text-[11.5px] text-[#9C9A9B]">
            {hasFiles ? "Add more files anytime" : "Files stay on this page"}
          </p>
          <h2 className="font-[Montserrat,sans-serif] text-[17px] font-bold text-white">
            {hasFiles ? "Add or update files for" : "Upload files for"} {caseId}
          </h2>
          <p className="mt-1 text-[13px] text-[#9C9A9B]">
            You can reopen a completed case later (for example months after
            surgery) from My Cases → upload icon to add IOS scans for custom
            abutment design. Each zone only accepts its listed types.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        {SECTIONS.map((section) => (
          <div
            key={section.id}
            className="flex flex-col gap-2.5 rounded-xl border border-[#303136] bg-[#24252a] p-3.5"
          >
            <div className="text-[13.5px] font-semibold text-[#d6d4d5]">
              {section.title}
              {section.required ? (
                <span className="ml-0.5 font-bold text-[#E5A443]">*</span>
              ) : (
                <span className="ml-1.5 text-[11.5px] font-normal text-[#9C9A9B]">
                  if available
                </span>
              )}
              <span className="mt-0.5 block text-[11.5px] font-normal text-[#9C9A9B]">
                {section.subtitle}
              </span>
            </div>
            <FileUploader
              userId={user?.user_id || 0}
              caseId={caseId}
              file_type="CLIENT_UPLOAD"
              caseFolderId={
                caseFolderId != null ? String(caseFolderId) : null
              }
              accessToken={boxAccessToken || ""}
              caseOwner={caseOwner}
              variant="dark"
              tone="uris"
              pathPrefix={section.folder}
              accept={section.accept}
              allowedExtensions={[...section.allowed]}
              dropTitle="Drop file here or browse"
              dropHint={section.hint}
            />
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-col items-end gap-2">
        {!fetching && !hasFiles ? (
          <p className="text-sm text-[#EDB766]">
            Upload at least one file before completing the case.
          </p>
        ) : null}
        <button
          type="button"
          disabled={fetching || !hasFiles}
          onClick={() =>
            user?.role?.toLowerCase() === "user"
              ? navigate("/dashboard/my-cases")
              : navigate("/dashboard/admin/cases")
          }
          className="h-[46px] rounded-[9px] border border-[#BF1E2E] bg-[#BF1E2E] px-8 text-sm font-bold text-white hover:bg-[#a51927] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {hasFiles ? "Done" : "Complete case"}
        </button>
      </div>
    </div>
  );
};

export default UrisCaseFiles;
